import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Single in-flight /auth/me promise — dedupes the double mount (React StrictMode)
// so guests see exactly one auth check per page load.
let mePromise = null;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      if (!mePromise) mePromise = axios.get(`${API}/auth/me`, { withCredentials: true });
      const response = await mePromise;
      setUser(response.data);
    } catch (error) {
      // Not logged in — if the temporary open-access window is on, silently
      // establish a per-browser guest identity so login-gated features work.
      try {
        const status = await axios.get(`${API}/open-access/status`);
        if (status.data?.active) {
          const guest = await axios.post(`${API}/auth/guest-session`, {}, { withCredentials: true });
          setUser(guest.data);
        } else {
          setUser(null);
        }
      } catch (e) {
        setUser(null);
      }
    } finally {
      mePromise = null;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    // Hard email verification: registration no longer logs the user in
    if (!response.data.verification_required) setUser(response.data);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, loginWithGoogle, exchangeSession, logout, checkAuth,
      refreshUser: checkAuth,
      isAuthenticated: !!user, isAdmin: user?.role === 'admin', isGuest: !!user?.is_guest
    }}>
      {children}
    </AuthContext.Provider>
  );
};
